import { useCreateAccount, usePermissions, useUpdateAccount } from '@e2m-customer-portal/hooks';
import { PermissionId, Validation, ValidationMode, isEmail, isRequired, validation } from '@e2m-customer-portal/shared';
import { Dialog, DialogProps, TextInput, ValidatedContainer } from '@e2m-customer-portal/ui';
import { Button, DialogActions, DialogContent, Divider, FormControl, InputLabel, Typography } from '@mui/material';
import { MouseEventHandler, ReactElement, SyntheticEvent, useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MailIcon } from '../../../assets/icons';
import { Account, AssignedPermissionInput } from '../../../generated/graphql';
import { isGranted as checkGranted, getErrorTranslationKey } from '../../../utils';
import { PersonalSpaceSwitch } from '../personal-space-switch/PersonalSpaceSwitch';
import { MarketLocationSelect } from './MarketLocationSelect';

const adminImplicitPermissions: readonly string[] = [
  PermissionId.seeForecast,
  PermissionId.editForecast,
  PermissionId.seeRevenue,
  PermissionId.seeAllMarketLocations,
];

export type DialogAccount = Omit<Account, 'permissions'> & Record<'permissions', readonly AssignedPermissionInput[]>;

type Reason = 'backdropClick' | 'escapeKeyDown' | 'cancelClick' | 'successClick';
type OnCloseFunc<E extends SyntheticEvent = SyntheticEvent> = (event: E | null, reason: Reason) => void;

export interface UserDialogProps {
  variant: 'create' | 'edit';
  open: boolean;
  onClose: OnCloseFunc;
  account: DialogAccount;
  onChange: (account: DialogAccount) => void;
}

export function UserDialog({
  variant,
  open,
  onClose,
  account,
  onChange,
}: Readonly<UserDialogProps>): ReactElement<UserDialogProps> {
  const [t] = useTranslation();
  const [tv] = useTranslation('validation');
  const marketLocationSelectLabelId = useId();

  const [validationMode, setValidationMode] = useState<ValidationMode>('on');
  const [isValid, setIsValid] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [existingEmail, setExistingEmail] = useState<string | undefined>(undefined);

  const ownPermissions = usePermissions().permissions;
  const createAccount = useCreateAccount();
  const updateAccount = useUpdateAccount();

  useEffect(() => {
    if (open) {
      setValidationMode('on');
    }
  }, [open]);

  const isSelfGranted = checkGranted(ownPermissions);
  const isGranted = checkGranted(account.permissions);

  const togglePermission = (permissionId: string) => () => {
    const granted = !account.permissions.find((permission) => permission.permissionId === permissionId)?.granted;
    onChange({
      ...account,
      permissions: account.permissions.map((permission) => {
        if (
          permissionId === PermissionId.configureUsers &&
          adminImplicitPermissions.includes(permission.permissionId)
        ) {
          return {
            ...permission,
            granted,
          };
        }
        if (
          permissionId === PermissionId.seeForecast &&
          permission.permissionId === PermissionId.editForecast &&
          !granted
        ) {
          return {
            ...permission,
            granted: false,
          };
        }
        if (
          permissionId === PermissionId.editForecast &&
          permission.permissionId === PermissionId.seeForecast &&
          granted
        ) {
          return {
            ...permission,
            granted: true,
          };
        }
        if (permission.permissionId === permissionId) {
          return {
            ...permission,
            granted,
          };
        }
        return permission;
      }),
    });
  };

  const isUnusedEmail = (message?: string): Validation => {
    return validation(message ?? 'The given email already exists.', (value) => {
      return value !== existingEmail;
    });
  };

  const confirmClick = async () => {
    if (isValid) {
      setIsLoading(true);
      const action =
        variant === 'create'
          ? createAccount(
              account.email,
              account.name,
              account.permissions,
              account.marketLocations?.map((marketLocation) => marketLocation.id),
            )
          : updateAccount(
              account.email,
              account.name,
              account.permissions,
              account.marketLocations?.map((marketLocation) => marketLocation.id),
            );
      try {
        const account = await action;
        if (account) {
          setValidationMode('off');
          onClose(null, 'successClick');
        }
      } catch (error: unknown) {
        const errorTranslationKey = getErrorTranslationKey(error);
        if (errorTranslationKey === 'ACCOUNT_CONFLICT') {
          setValidationMode('on');
          setExistingEmail(account.email);
        }
      } finally {
        setIsLoading(false);
      }
    } else {
      setValidationMode('on');
    }
  };

  const handleClose: DialogProps['onClose'] = (event, reason): void => {
    if (reason === 'backdropClick') {
      return;
    }

    setValidationMode('off');
    onClose(event, reason);
  };

  const onCancel: MouseEventHandler<HTMLButtonElement> = (event) => handleClose(event, 'cancelClick');

  return (
    <Dialog
      title={
        variant === 'edit'
          ? t('SETTINGS_USER_MANAGEMENT_EDIT_USER_TITLE')
          : t('SETTINGS_USER_MANAGEMENT_NEW_USER_TITLE')
      }
      open={open}
      onClose={handleClose}
    >
      <DialogContent className="flex flex-col gap-y-3">
        <ValidatedContainer validate={validationMode} onValidated={setIsValid}>
          <TextInput
            value={account?.name}
            name="name"
            label={t('SHARED_NAME')}
            required
            onChange={(value) => onChange({ ...account, name: value })}
            disabled={isLoading}
          />

          <TextInput
            value={account.email}
            name="email"
            label={t('SHARED_EMAIL')}
            type="email"
            rules={[
              isEmail(tv('COMMON_EMAIL')),
              isRequired(tv('COMMON_REQUIRED')),
              isUnusedEmail(t('ACCOUNT_CONFLICT')),
            ]}
            endAdornment={<MailIcon className="text-black" />}
            required
            onChange={(value) => onChange({ ...account, email: value })}
            disabled={isLoading || variant === 'edit'}
          />
          <Typography variant="h3" className="mb-2 mt-4">
            {t('SETTINGS_USER_INFO_PERMISSIONS')}
          </Typography>
          {isSelfGranted(PermissionId.configureAdmins) && (
            <FormControl>
              <PersonalSpaceSwitch
                checked={isGranted(PermissionId.configureUsers)}
                switchTitle={t('SETTINGS_USER_INFO_CONFIGURE_USERS')}
                switchInfoAllowed={t('SETTINGS_USER_INFO_CONFIGURE_USERS_ALLOWED', {
                  name: account.name || t('SETTINGS_USER_INFO_NAME_PLACEHOLDER'),
                })}
                switchInfoForbidden={t('SETTINGS_USER_INFO_CONFIGURE_USERS_FORBIDDEN', {
                  name: account.name || t('SETTINGS_USER_INFO_NAME_PLACEHOLDER'),
                })}
                onChange={togglePermission(PermissionId.configureUsers)}
                disabled={isLoading}
              />
            </FormControl>
          )}
          {/* for the select component we cannot use conditional rendering because it will not update the 'required' prop
          as workaround we add a hidden class to the select component and the related elements */}
          <Divider className={isGranted(PermissionId.configureUsers) ? 'hidden' : undefined} />

          <FormControl required>
            <InputLabel
              id={marketLocationSelectLabelId}
              className={isGranted(PermissionId.configureUsers) ? 'hidden' : undefined}
            >
              {t('SETTINGS_USER_MANAGEMENT_MARKET_LOCATIONS')}
            </InputLabel>

            <MarketLocationSelect
              className={isGranted(PermissionId.configureUsers) ? 'hidden' : undefined}
              name={t('SETTINGS_USER_MANAGEMENT_MARKET_LOCATIONS')}
              value={account.marketLocations?.map((marketLocation) => marketLocation.id) ?? []}
              account={account}
              onChange={onChange}
              labelId={marketLocationSelectLabelId}
              disabled={isLoading}
              required={!isGranted(PermissionId.configureUsers)}
            />
          </FormControl>

          {!isGranted(PermissionId.configureUsers) && (
            <>
              <Divider />
              <PersonalSpaceSwitch
                checked={isGranted(PermissionId.seeForecast)}
                switchTitle={t('SETTINGS_SALES_PARTNER_SEE_FORECAST')}
                switchInfoAllowed={t('SETTINGS_USER_INFO_SEE_FORECAST_ALLOWED', {
                  name: account.name || t('SETTINGS_USER_INFO_NAME_PLACEHOLDER'),
                })}
                switchInfoForbidden={t('SETTINGS_USER_INFO_SEE_FORECAST_FORBIDDEN', {
                  name: account.name || t('SETTINGS_USER_INFO_NAME_PLACEHOLDER'),
                })}
                onChange={togglePermission(PermissionId.seeForecast)}
                disabled={isLoading}
              />

              <Divider />

              <PersonalSpaceSwitch
                checked={isGranted(PermissionId.editForecast)}
                switchTitle={t('SETTINGS_SALES_PARTNER_EDIT_FORECAST')}
                switchInfoAllowed={t('SETTINGS_USER_INFO_EDIT_FORECAST_ALLOWED', {
                  name: account.name || t('SETTINGS_USER_INFO_NAME_PLACEHOLDER'),
                })}
                switchInfoForbidden={t('SETTINGS_USER_INFO_EDIT_FORECAST_FORBIDDEN', {
                  name: account.name || t('SETTINGS_USER_INFO_NAME_PLACEHOLDER'),
                })}
                onChange={togglePermission(PermissionId.editForecast)}
                disabled={isLoading}
              />

              <Divider />

              <PersonalSpaceSwitch
                checked={isGranted(PermissionId.seeRevenue)}
                switchTitle={t('SETTINGS_SALES_PARTNER_SEE_REVENUE')}
                switchInfoAllowed={t('SETTINGS_USER_INFO_SEE_REVENUE_ALLOWED', {
                  name: account.name || t('SETTINGS_USER_INFO_NAME_PLACEHOLDER'),
                })}
                switchInfoForbidden={t('SETTINGS_USER_INFO_SEE_REVENUE_FORBIDDEN', {
                  name: account.name || t('SETTINGS_USER_INFO_NAME_PLACEHOLDER'),
                })}
                onChange={togglePermission(PermissionId.seeRevenue)}
                disabled={isLoading}
              />
            </>
          )}
        </ValidatedContainer>
      </DialogContent>

      <DialogActions>
        <Button color="primary" onClick={onCancel} disabled={isLoading} variant="outlined">
          {t('SHARED_CANCEL')}
        </Button>

        <Button color="primary" onClick={confirmClick} disabled={isLoading}>
          {t('SHARED_SAVE')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
